/**
 * Reports ([ADR 0024](../../../docs/adr/0024-a-report-is-filed-from-where-the-problem-is-and-fixed-means-a-build.md)): the
 * shapes the customer's app, the desk and the API agree on. One word in both products: a customer's *report*, with a
 * thread under it, five states and one sentence per move.
 */

export const REPORT_KINDS = ["problem", "idea", "question"] as const;
export type ReportKind = (typeof REPORT_KINDS)[number];
export const REPORT_STATUSES = ["open", "looking", "fixed", "closed", "not_a_bug", "wont_do"] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];
export type ReportAuthor = "customer" | "operator" | "system";
/** The moves an operator makes by hand. Never `fixed`: a release that names the report does that (ADR 0024 §5). */
export const DESK_REPORT_MOVES = ["open", "looking", "not_a_bug", "wont_do", "closed"] as const;
export type DeskReportMove = (typeof DESK_REPORT_MOVES)[number];
/** How long each thing a report carries may be. The API clips the context and refuses the rest. */
export const REPORT_LIMITS = { subject: 200, body: 10_000, route: 500, screen: 40, reference: 40 } as const;

/**
 * What the app attached, and what the API stamped. Identifiers only (ADR 0024 §3): the project's id, never its brief.
 * `route` includes the query string, and since 2026-09-20 its path is the tab (`/studio`); `screen` names the tab
 * too, and goes on saying it for a report filed from an address that names none, such as an email's `/?report=…`.
 */
export interface ReportContext {
  route: string | null;
  screen: string | null;
  projectId: string | null;
  reference: string | null;
  version: string;
  sha: string;
}

/** `POST /v1/reports`. The workspace, the person and the build come from the session and the API, never from here. */
export interface NewReport {
  kind: ReportKind;
  subject: string;
  body: string;
  route?: string | undefined;
  screen?: string | undefined;
  projectId?: string | undefined;
  reference?: string | undefined;
}

export interface ReportView {
  id: number;
  kind: ReportKind;
  status: ReportStatus;
  subject: string;
  body: string;
  context: ReportContext;
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
  /** Shown while the report stands fixed or closed: "fixed in 0.15.0" beside "looking at it" would be two answers. */
  fixedInVersion: string | null;
}

export interface ReportsResponse { reports: ReportView[] }

export interface ReportMessageView {
  id: number;
  authorKind: ReportAuthor;
  body: string;
  /** The move this message made, when it made one. */
  status: ReportStatus | null;
  at: string;
}

export interface ReportDetailResponse { report: ReportView; messages: ReportMessageView[] }

/**
 * One thing Joogoos said on a report: an answer, a move, or the release that fixed it. What the button beside the credit
 * lists (ADR 0024, amended 2026-10-05), and what is emailed as well only to someone who asked.
 */
export interface ReportNewsItem {
  reportId: number;
  /** The report's subject, so the line says which report without opening it. */
  subject: string;
  /** Joogoos's sentence, or "Fixed in version 0.39.0." */
  said: string;
  /** The move it made, when it made one. */
  status: ReportStatus | null;
  at: string;
  /** Said after this person last opened the news. */
  isNew: boolean;
}

/** `GET /v1/reports/news`. `emails` is this person's own switch, the one Settings draws. */
export interface ReportNewsResponse { unseen: number; items: ReportNewsItem[]; emails: boolean }

/** One release as What's new draws it: only what was added and fixed, never the Other section. */
export interface WhatsNewVersion { version: string; at: string; added: string[]; fixed: string[] }
export interface WhatsNewResponse { versions: WhatsNewVersion[] }

/** A report on the desk: who, where and when, and whether the customer is waiting on an answer. */
export interface DeskReportRow {
  id: number;
  workspaceId: string;
  workspaceName: string | null;
  kind: ReportKind;
  status: ReportStatus;
  subject: string;
  filedBy: string | null;
  createdAt: string;
  updatedAt: string;
  notifiedAt: string | null;
  route: string | null;
  screen: string | null;
  version: string | null;
  reference: string | null;
  fixedInVersion: string | null;
  messages: number;
  lastAuthor: ReportAuthor | null;
  /** Open or being looked at, or the customer spoke last on one that is not closed. */
  waiting: boolean;
}

export interface DeskReportsResponse {
  window: "waiting" | "all";
  counts: { open: number; looking: number; fixed: number; waiting: number; total: number };
  flags: string[];
  reports: DeskReportRow[];
}

export interface DeskReportMessage extends ReportMessageView { author: string | null; notifiedAt: string | null }

export interface DeskReportDetailResponse {
  report: DeskReportRow & { body: string; context: ReportContext };
  messages: DeskReportMessage[];
}
